import React, { useState, useEffect } from 'react';
import { faMagnifyingGlass, faUser, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { PacienteService } from '../services/pacienteService';

interface Paciente {
  id: string;
  nome: string;
  celular?: string;
}

export default function QuickPacienteAccess(): React.ReactElement {
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const searchPacientes = async (): Promise<void> => {
      if (!debouncedQuery.trim() || !currentOrganization) {
        setPacientes([]);
        return;
      }

      setLoading(true);
      try {
        const response = await PacienteService.searchPacientes(
          currentOrganization.id,
          debouncedQuery,
          5
        );

        if (response.success && response.data) {
          setPacientes(
            response.data.map(p => ({
              id: p.id,
              nome: p.nome || '',
              celular: p.celular
            }))
          );
        } else {
          setPacientes([]);
        }
      } catch (error) {
        setPacientes([]);
      } finally {
        setLoading(false);
      }
    };

    searchPacientes();
  }, [debouncedQuery, currentOrganization]);

  const handlePacienteClick = (pacienteId: string): void => {
    navigate(`/pacientes/${pacienteId}`);
    setQuery('');
    setPacientes([]);
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Busca Rápida de Pacientes</h3>

      <div className="relative">
        <div className="relative">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
          />
          <input
            type="text"
            placeholder="Digite o nome do paciente..."
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {(loading || pacientes.length > 0) && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
            {loading ? (
              <div className="p-4 text-center">
                <img className="w-6 h-6 mx-auto" src="/loading.svg" alt="loading" />
              </div>
            ) : (
              <div className="py-1">
                {pacientes.map(paciente => (
                  <button
                    key={paciente.id}
                    onClick={() => handlePacienteClick(paciente.id)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between group"
                  >
                    <div className="flex items-center space-x-3">
                      <FontAwesomeIcon icon={faUser} className="text-gray-400 w-4 h-4" />
                      <div>
                        <p className="font-medium text-gray-900">{paciente.nome}</p>
                        {paciente.celular && (
                          <p className="text-sm text-gray-500">{paciente.celular}</p>
                        )}
                      </div>
                    </div>
                    <FontAwesomeIcon
                      icon={faArrowRight}
                      className="text-gray-400 group-hover:text-gray-600 w-4 h-4"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {query && !loading && pacientes.length === 0 && (
        <p className="text-gray-500 text-sm mt-2">Nenhum paciente encontrado</p>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={() => navigate('/pacientes')}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-2"
        >
          <span>Ver todos os pacientes</span>
          <FontAwesomeIcon icon={faArrowRight} className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
